import type {
  FormFieldControlMessage,
  FormFieldCustomMessage,
  FormFieldMessage,
  FormFieldMessageContextState,
} from '../types/formfield-messages.types.js';

export const enum FormFieldMessagesAction {
  ADDMESSAGE = 'addmessage',
  REMOVEMESSAGE = 'removemessage',
  DISABLEMESSAGE = 'disablemessage',
  SETISTOUCHED = 'setistouched',
  ADDFORMCONTROLID = 'addformcontrolid',
  REMOVEFORMCONTROLID = 'removeformcontrolid',
}

export type FormFieldMessagesReducerAction =
  | {
      type: FormFieldMessagesAction.ADDMESSAGE;
      payload: (
        | Omit<FormFieldControlMessage, 'id'>
        | FormFieldCustomMessage
      ) & { showError?: boolean };
    }
  | {
      type: FormFieldMessagesAction.REMOVEMESSAGE;
      payload: Pick<FormFieldMessage, 'id' | 'messageType' | 'controlId'>;
    }
  | {
      type: FormFieldMessagesAction.DISABLEMESSAGE;
      payload: {
        isDisabled: boolean | undefined;
      };
    }
  | {
      type: FormFieldMessagesAction.SETISTOUCHED;
      payload: { isTouched: boolean };
    }
  | {
      type: FormFieldMessagesAction.ADDFORMCONTROLID;
      payload: { controlId: string };
    }
  | {
      type: FormFieldMessagesAction.REMOVEFORMCONTROLID;
      payload: { controlId: string };
    };

type HTMLFormControlElement =
  | HTMLSelectElement
  | HTMLInputElement
  | HTMLTextAreaElement;

/**
 * Typeguard function that will check if message is a formcontrol (internal) message.
 * @internal
 */
function isFormControlPayload(
  payload:
    | Omit<FormFieldControlMessage, 'id'>
    | FormFieldCustomMessage
    | FormFieldControlMessage,
  messageType?: 'formcontrol' | 'custom',
): payload is FormFieldControlMessage | Omit<FormFieldControlMessage, 'id'> {
  return messageType === 'formcontrol';
}

/**
 * Typeguard function that will check if the element is a form control.
 * @internal
 */
function isHTMLFormControlElement(
  element: null | HTMLElement,
): element is HTMLFormControlElement {
  return !!(element && 'setCustomValidity' in element);
}

/**
 * Set custom validity on the HTML form control element.
 * @param id
 * @param isValid
 * @internal
 */
function setHTMLElementValidity(id: string, isValid: boolean) {
  const element = document.getElementById(id);
  if (isHTMLFormControlElement(element)) {
    element.setCustomValidity(isValid ? '' : ' ');
  }
}

/**
 * Function that will determine the validity of a form control.
 * @internal
 */
function updateValidity(
  state: FormFieldMessageContextState,
  explicitControlId?: string,
) {
  const { customMessages, formControlMessages, isTouched, isDisabled } = state;

  const hasCustomError = customMessages.some(
    (message) => message.variant === 'error',
  );

  const hasFormControlError = formControlMessages.some(
    (message) => message.variant === 'error',
  );

  const newIsValid = !hasCustomError && !hasFormControlError;
  const newShowError =
    !isDisabled && (state.showError || (isTouched ?? false)) && !newIsValid;

  // Only errors the browser has no native knowledge of should be reflected via
  // setCustomValidity(). These are:
  //   1. Developer-added errors (customMessages)
  //   2. Strato-internal custom errors (formControlMessages with validityStateKey 'customError'),
  //      e.g. TimeframeSelector's 'invalidOrder'
  //
  // Browser-native errors (valueMissing, typeMismatch, patternMismatch, …) must NOT be
  // passed to setCustomValidity() — the browser already tracks them, and adding a
  // redundant customError would prevent checkValidity() from returning true once
  // the native constraint is satisfied.
  const hasStratoCustomError = formControlMessages.some(
    (message) =>
      message.variant === 'error' && message.validityStateKey === 'customError',
  );
  const nativeValidityIsValid = !hasCustomError && !hasStratoCustomError;

  if (explicitControlId) {
    setHTMLElementValidity(explicitControlId, nativeValidityIsValid);

    return {
      ...state,
      isValid: newIsValid,
      showError: newShowError,
    };
  }

  state.controlIds?.forEach((id) =>
    setHTMLElementValidity(id, nativeValidityIsValid),
  );

  return {
    ...state,
    isValid: newIsValid,
    showError: newShowError,
  };
}

/** FormFieldMessage reducer function used for managing FormFieldMessages and FormFieldCustomMessages. */
export function formFieldMessagesReducer(
  state: FormFieldMessageContextState,
  action: FormFieldMessagesReducerAction,
): FormFieldMessageContextState {
  const newState = { ...state };

  // handle adding of form messages
  if (action.type === FormFieldMessagesAction.ADDMESSAGE) {
    const { payload } = action;
    const { messageType, ...remainingPayload } = payload;

    // Is a browser/strato message
    if (isFormControlPayload(remainingPayload, messageType)) {
      if (
        remainingPayload.validityStateKey === 'customError' &&
        remainingPayload.variant === 'error' &&
        'id' in remainingPayload
      ) {
        const { id: internalId, ...internalRest } =
          remainingPayload as typeof remainingPayload & { id: string };

        const customErrors = state.formControlMessages.filter(
          (m) => m.validityStateKey === 'customError',
        );

        // If the same sole customError is already present and showError won't
        // change, return early to keep the array reference stable and prevent
        // a useEffect re-run → re-dispatch → infinite-render loop.
        if (customErrors.length === 1 && customErrors[0].id === internalId) {
          if ((payload.showError ?? state.showError) === state.showError) {
            return state;
          }
        } else {
          // Replace all existing customErrors with the new one so only one
          // internal error is shown at a time.
          newState.formControlMessages = [
            ...state.formControlMessages.filter(
              (m) => m.validityStateKey !== 'customError',
            ),
            { id: internalId, ...internalRest },
          ];
        }
      } else {
        // Browser validity errors are keyed by validityStateKey; only add once.
        const isNewBrowserError =
          remainingPayload.validityStateKey !== undefined &&
          !state.formControlMessages.some(
            (message) =>
              message.validityStateKey === remainingPayload.validityStateKey,
          );

        if (!isNewBrowserError) {
          return state;
        }

        newState.formControlMessages = [
          ...newState.formControlMessages,
          { id: `strato-${crypto.randomUUID()}`, ...remainingPayload },
        ];
      }

      // Is a custom error
    } else if (
      'id' in remainingPayload &&
      !state.formControlMessages.some(
        (message) => message.id === remainingPayload.id,
      ) &&
      !state.customMessages.some(
        (message) => message.id === remainingPayload.id,
      )
    ) {
      newState.customMessages.push(remainingPayload);
    }

    if (remainingPayload.variant === 'error') {
      newState.showError = payload.showError ?? state.showError;
    }

    return updateValidity(newState, payload.controlId);
  }

  if (action.type === FormFieldMessagesAction.REMOVEMESSAGE) {
    const { payload } = action;
    let didUpdate = false;

    if (payload.messageType === 'formcontrol') {
      newState.formControlMessages = newState.formControlMessages.filter(
        (message) => {
          if (message.id === payload.id) {
            didUpdate = true;
          }

          return message.id !== payload.id;
        },
      );
    } else {
      newState.customMessages = newState.customMessages.filter((message) => {
        if (message.id === payload.id) {
          didUpdate = true;
        }

        return message.id !== payload.id;
      });
    }

    // avoid unnecessary updates and possible endless loops by early escaping if nothing was removed.
    if (!didUpdate) {
      return state;
    }

    return updateValidity(newState, payload.controlId);
  }

  if (action.type === FormFieldMessagesAction.DISABLEMESSAGE) {
    const { payload } = action;
    newState.isDisabled = payload.isDisabled;
  }

  if (action.type === FormFieldMessagesAction.SETISTOUCHED) {
    const { payload } = action;
    newState.isTouched = payload.isTouched;
  }

  if (action.type === FormFieldMessagesAction.ADDFORMCONTROLID) {
    const {
      payload: { controlId },
    } = action;

    newState.controlIds ??= [];

    if (!newState.controlIds.includes(controlId)) {
      newState.controlIds.push(controlId);
    }
  }

  if (action.type === FormFieldMessagesAction.REMOVEFORMCONTROLID) {
    const {
      payload: { controlId },
    } = action;

    newState.controlIds = state.controlIds?.filter((id) => id !== controlId);
  }

  return updateValidity(newState);
}
